import {
  FC,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  Ref,
  cloneElement,
  useRef,
  useState,
} from 'react';

import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';

export type TDropHalf = 'top' | 'bottom';

interface IDragItem {
  id: string;
  // Index at drag start (the list only moves on drop).
  index: number;
}

type TRowElement = ReactElement<{
  className?: string;
  ref?: Ref<HTMLTableRowElement>;
  tabIndex?: number;
  onKeyDown?: (event: KeyboardEvent<HTMLTableRowElement>) => void;
  'aria-keyshortcuts'?: string;
  'data-drag-id'?: string;
}>;

const KEY_STEPS: Record<string, number> = { ArrowUp: -1, ArrowDown: 1 };

// Prefixed by the list type: a policy and a rule can share a numeric id.
const dragId = (type: string, id: string) => `${type}:${id}`;

// The moved row remounts (the table re-keys its body), so focus is restored by id.
const refocus = (type: string, id: string) =>
  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>(`[data-drag-id="${dragId(type, id)}"]`)?.focus();
  });

// Index after the move when dropped on the top or bottom half of another row.
export const getDropIndex = (fromIndex: number, hoverIndex: number, half: TDropHalf): number => {
  const target = half === 'bottom' ? hoverIndex + 1 : hoverIndex;

  // Removing the source first shifts everything below it up by one.
  return fromIndex < target ? target - 1 : target;
};

const joinClasses = (...classes: (string | undefined)[]) => classes.filter(Boolean).join(' ');

interface IDraggableRowProps {
  // Rows only reorder within their own list.
  type: string;
  id: string;
  index: number;
  // Cloned with the ref and the drag state classes.
  row: TRowElement;
  onMove?: (id: string, toIndex: number) => void;
  // Rendered after the row (rules row, drop zone).
  children?: ReactNode;
}

// The Policies page's row drag-and-drop: the whole row drags and drops; CSS fades it
// while dragging and marks the landing edge. Drops apply at once (local edit).
// Keyboard: a focused row moves one step with Alt+ArrowUp / Alt+ArrowDown.
export const DraggableRow: FC<IDraggableRowProps> = ({
  type,
  id,
  index,
  row,
  onMove,
  children,
}) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [half, setHalf] = useState<TDropHalf>('top');

  // Which half of this row the pointer is over; null without geometry.
  const halfAt = (monitor: DropTargetMonitor): TDropHalf | null => {
    const rect = rowRef.current?.getBoundingClientRect();
    const offset = monitor.getClientOffset();
    if (!rect || !offset) return null;
    return offset.y - rect.top < rect.height / 2 ? 'top' : 'bottom';
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type,
      item: { id, index } as IDragItem,
      collect: monitor => ({ isDragging: monitor.isDragging() }),
    }),
    [type, id, index],
  );

  const [{ isOver }, drop] = useDrop<IDragItem, void, { isOver: boolean }>(
    () => ({
      accept: type,
      canDrop: item => item.id !== id,
      hover: (_item, monitor) => {
        const next = halfAt(monitor);
        if (next) setHalf(next);
      },
      // Measured again at drop: the hover state can lag one event behind a quick drop.
      drop: (item, monitor) => {
        onMove?.(item.id, getDropIndex(item.index, index, halfAt(monitor) ?? half));
      },
      collect: monitor => ({ isOver: monitor.isOver({ shallow: true }) && monitor.canDrop() }),
    }),
    [type, id, index, half, onMove],
  );

  drag(rowRef);
  drop(rowRef);

  const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    const step = KEY_STEPS[event.key];
    if (!event.altKey || step === undefined || event.target !== event.currentTarget) return;
    event.preventDefault();
    onMove?.(id, index + step);
    refocus(type, id);
  };

  return (
    <>
      {cloneElement(row, {
        ref: rowRef,
        tabIndex: 0,
        onKeyDown,
        'aria-keyshortcuts': 'Alt+ArrowUp Alt+ArrowDown',
        'data-drag-id': dragId(type, id),
        className: joinClasses(
          row.props.className,
          'draggable-row',
          isDragging ? 'dragging' : undefined,
          isOver ? `drop-target-${half}` : undefined,
        ),
      })}
      {children}
    </>
  );
};

interface IDropZoneRowProps {
  type: string;
  // A drop here moves the item last.
  count: number;
  // Empty full-width row from the table.
  row: TRowElement;
  onMove?: (id: string, toIndex: number) => void;
}

// The Policies page's zero-height row after the last item: catches drops below the list.
export const DropZoneRow: FC<IDropZoneRowProps> = ({ type, count, row, onMove }) => {
  const [{ isOver }, drop] = useDrop<IDragItem, void, { isOver: boolean }>(
    () => ({
      accept: type,
      drop: item => {
        onMove?.(item.id, count - 1);
      },
      collect: monitor => ({ isOver: monitor.isOver({ shallow: true }) }),
    }),
    [type, count, onMove],
  );

  return cloneElement(row, {
    ref: drop,
    className: joinClasses(
      row.props.className,
      'drop-zone-row',
      isOver ? 'drop-target-top' : undefined,
    ),
  });
};
